import { Injectable, computed, signal } from "@angular/core";
import { ActivatedRouteSnapshot, NavigationEnd, NavigationStart, Router, RouterEvent, RoutesRecognized } from "@angular/router";
import { filter, Observable, pairwise, Subject } from "rxjs";

export interface ProductCategoryDisplayProp {
  show: boolean;
  disabled: boolean;
}

export interface EntityDataMetadata {
  branchSetCode?: string;
  ownedBy?: string;
  createdDate?: string;
  createdBy?: string;
}
@Injectable({
  providedIn: "root",
})
export class CustomHeaderService {
  private _title = signal<string>("");
  readonly title = computed(() => this._title());

  private _newEntityRouterLink = signal<string>("");
  private _showRouterLink = signal<boolean>(false);
  readonly newEntityRouterLink = computed(() => this._newEntityRouterLink());
  readonly showRouterLink = computed(() => this._showRouterLink());

  private _badge = signal<string>("");
  readonly badge = computed(() => this._badge());

  private _adaptiveUIWidgetTitle = signal<string>("");
  readonly adaptiveUIWidgetTitle = computed(() => this._adaptiveUIWidgetTitle());

  private _showAddChildAdaptiveUI = signal<boolean>(false);
  readonly showAddChildAdaptiveUIReadonly = computed(() => this._showAddChildAdaptiveUI());

  $addChildAdaptiveUI = new Subject<boolean>();

  private _showProductCategory = signal<ProductCategoryDisplayProp>({
    show: false,
    disabled: false,
  });
  readonly showProductCategoryReadonly = computed(() => this._showProductCategory());

  private _showEntityDataMetadata = signal<EntityDataMetadata>({} as EntityDataMetadata);
  readonly showEntityDataMetadataReadonly = computed(() => this._showEntityDataMetadata());

  setTitle(title: string) {
    this._title.set(title);
  }
  setBadge(title: string) {
    this._badge.set(title);
  }

  setAdaptiveUIWidgetTitle(title: string) {
    this._adaptiveUIWidgetTitle.set(title);
  }

  showProductCategory(disabled: boolean = false) {
    this._showProductCategory.set({ show: true, disabled: disabled });
  }

  hideProductCategory() {
    this._showProductCategory.set({ show: false, disabled: false });
  }

  showAddChildAdaptiveUI() {
    this._showAddChildAdaptiveUI.set(true);
  }

  hideAddChildAdaptiveUI() {
    this._showAddChildAdaptiveUI.set(false);
  }

  addChildAdaptiveUI() {
    this.$addChildAdaptiveUI.next(true);
  }

  publishEntityDataMetadata(entityDataMetadata: EntityDataMetadata) {
    this._showEntityDataMetadata.set(entityDataMetadata);
  }

  // setNewEntityRouterLink(newEntityRouterLink: string) {
  //   this._newEntityRouterLink.set(newEntityRouterLink);
  //   this._showRouterLink.set(true);
  // }

  setNewEntityRouterLink(newEntityRouterLink: string) {
    this._newEntityRouterLink.set(newEntityRouterLink);
    this._showRouterLink.set(true);
  }

  clear() {
    this.setTitle("");
    this.setNewEntityRouterLink("");
    this._showRouterLink.set(false);
    this.setBadge("");
    this.hideProductCategory();
    this.publishEntityDataMetadata({} as EntityDataMetadata);
  }

  constructor(public router: Router) {
    // router.events.pipe(filter((e) => e instanceof NavigationStart)).subscribe((e) => {
    //   const currentUrl = this.router.url;
    //   const newUrl = e.url;
    //   // check if newUrl is a child of currentUrl
    //   if (this.isChildUrl(currentUrl, newUrl)) {
    //     console.log(`${newUrl} is a child of ${currentUrl}`);
    //   } else {
    //     this.clear();
    //   }
    //   // console.log(e);
    //   // this.clear();
    // });

    // this.router.events
    //   .pipe(
    //     filter((e) => e instanceof RoutesRecognized),
    //     pairwise() // gives [previous, current]
    //   )
    //   .subscribe(([prev, next]: [RoutesRecognized, RoutesRecognized]) => {
    //     const prevSnapshot = prev.state.root;
    //     const nextSnapshot = next.state.root;

    //     if (this.isChildRoute(prevSnapshot, nextSnapshot)) {
    //       console.log(`${next.url} is a child of ${prev.url}`);
    //     } else {
    //       this.clear();
    //     }
    // });

    this.router.events
      .pipe(
        filter((e) => e instanceof RoutesRecognized),
        pairwise()
      )
      .subscribe(([prev, next]: [RoutesRecognized, RoutesRecognized]) => {
        const relation = this.compareUrls(prev.urlAfterRedirects, next.urlAfterRedirects);
        if (relation === "unrelated") {
          this.clear();
        }
      });
  }

  private compareUrls(prevUrl: string, nextUrl: string): "child" | "sibling" | "unrelated" {
    const lazyPrefixes = ["adaptive-ui-flow"]; // add other lazy prefixes if needed

    // Remove query params and leading/trailing slashes
    const clean = (url: string) => url.split("?")[0].replace(/^\/+|\/+$/g, "");

    const normalize = (segments: string[]) => segments.filter((seg) => seg && !lazyPrefixes.includes(seg));

    const prevSegs = normalize(clean(prevUrl).split("/"));
    const nextSegs = normalize(clean(nextUrl).split("/"));

    // Find common prefix length
    let i = 0;
    while (i < prevSegs.length && i < nextSegs.length && prevSegs[i] === nextSegs[i]) {
      i++;
    }

    if (i === prevSegs.length && nextSegs.length > prevSegs.length) {
      return "child"; // next extends prev
    }

    if (i > 0) {
      return "sibling"; // they share some common prefix but diverged
    }

    return "unrelated";
  }

  // private compareUrls(prevUrl: string, nextUrl: string): "child" | "sibling" | "unrelated" {
  //   // Remove query params and leading/trailing slashes
  //   const clean = (url: string) => url.split("?")[0].replace(/^\/+|\/+$/g, "");
  //   const prevSegs = clean(prevUrl).split("/");
  //   const nextSegs = clean(nextUrl).split("/");

  //   // Find common prefix length
  //   let i = 1;
  //   while (i < prevSegs.length && i < nextSegs.length && prevSegs[i] === nextSegs[i]) {
  //     i++;
  //   }

  //   if (i === prevSegs.length && nextSegs.length > prevSegs.length) {
  //     return "child"; // next extends prev
  //   }

  //   if (i > 0) {
  //     return "sibling"; // they share some common prefix but diverged
  //   }

  //   return "unrelated";
  // }

  /** Convert snapshot tree into flat array of path segments */
  private getPathSegments(snapshot: ActivatedRouteSnapshot): string[] {
    const segments: string[] = [];
    let node: ActivatedRouteSnapshot | null = snapshot;

    while (node) {
      if (node.url.length) {
        segments.push(...node.url.map((u) => u.path));
      }
      node = node.firstChild ?? null;
    }

    return segments;
  }

  /**
   * Compare previous vs next route:
   * - "child": next contains prev as prefix
   * - "sibling": diverge at same depth
   * - "unrelated": no meaningful relation
   */
  private compareRoutes(prev: ActivatedRouteSnapshot, next: ActivatedRouteSnapshot): "child" | "sibling" | "unrelated" {
    const prevSegs = this.getPathSegments(prev);
    const nextSegs = this.getPathSegments(next);

    let i = 0;
    while (i < prevSegs.length && i < nextSegs.length && prevSegs[i] === nextSegs[i]) {
      i++;
    }

    if (i === prevSegs.length && nextSegs.length > prevSegs.length) {
      return "child"; // next extends prev
    }

    if (i > 0 && i === prevSegs.length - 1 && i === nextSegs.length - 1) {
      return "sibling"; // diverged at last segment
    }

    return "unrelated";
  }

  /** Find the deepest leaf node of a route snapshot tree */
  // private getDeepestChild(snapshot: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
  //   let node = snapshot;
  //   while (node.firstChild) {
  //     node = node.firstChild;
  //   }
  //   return node;
  // }

  // /** Check if candidate is a descendant of parent */
  // private isChild(parent: ActivatedRouteSnapshot, candidate: ActivatedRouteSnapshot): boolean {
  //   let node: ActivatedRouteSnapshot | null = candidate.parent;
  //   while (node) {
  //     if (node === parent) {
  //       return true;
  //     }
  //     node = node.parent;
  //   }
  //   return false;
  // }

  // /** Check if both routes share the same parent but are not identical */
  // private isSibling(a: ActivatedRouteSnapshot, b: ActivatedRouteSnapshot): boolean {
  //   return a.parent && b.parent && a.parent === b.parent && a !== b;
  // }

  /**
   * Check if `parent` snapshot is an ancestor of `candidate`
   */
  // private isAncestor(parent: ActivatedRouteSnapshot, candidate: ActivatedRouteSnapshot): boolean {
  //   let node: ActivatedRouteSnapshot | null = candidate.parent;

  //   while (node) {
  //     if (node === parent) {
  //       return true; // found parent in the chain
  //     }
  //     node = node.parent;
  //   }
  //   return false;
  // }

  // private isChildUrl(parentUrl: string, candidateUrl: string): boolean {
  //   const normalize = (url: string) => url.split("?")[0].replace(/\/+$/, ""); // strip query + trailing slash

  //   const parent = normalize(parentUrl);
  //   const candidate = normalize(candidateUrl);

  //   return candidate.startsWith(parent + "/");
  // }

  // private isChildRoute(parent: ActivatedRouteSnapshot, candidate: ActivatedRouteSnapshot): boolean {
  //   const parentPath = this.getFullPath(parent);
  //   const candidatePath = this.getFullPath(candidate);

  //   // strict hierarchy check: candidate must contain parent as prefix path
  //   return parentPath.length > 0 && candidatePath.length > parentPath.length && candidatePath.slice(0, parentPath.length).join("/") === parentPath.join("/");
  // }

  // private getFullPath(snapshot: ActivatedRouteSnapshot): string[] {
  //   const segments: string[] = [];
  //   let current: ActivatedRouteSnapshot | null = snapshot;

  //   while (current) {
  //     if (current.routeConfig?.path) {
  //       segments.push(current.routeConfig.path);
  //     }
  //     current = current.firstChild ?? null;
  //   }

  //   return segments;
  // }
}
